import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  MeetingSection, AgendaTemplateSummary, AvailableSection, SectionType, MeetingAttendee
} from '../../types'
import UserAvatar from '../shared/UserAvatar'

interface SectionItem {
  id?: number
  section_type: SectionType
  display_name: string
  title?: string | null
  duration_minutes: number | null
  is_heading: boolean
}

interface Props {
  meetingId: number
  meetingDisplayName?: string | null
  sections: MeetingSection[]
  templates: AgendaTemplateSummary[]
  availableSections: AvailableSection[]
  currentTemplateId: number | null
  canFacilitate: boolean
  canStart: boolean
  canSaveAsTemplate?: boolean
  attendees: MeetingAttendee[]
}

export default function MeetingSetup({
  meetingId, meetingDisplayName, sections, templates, availableSections, currentTemplateId, canFacilitate,
  canStart, canSaveAsTemplate = false, attendees
}: Props) {
  const [items, setItems] = useState<SectionItem[]>(
    sections.map(s => ({
      id: s.id,
      section_type: s.section_type,
      display_name: s.display_name,
      title: s.title || null,
      duration_minutes: s.is_heading ? null : (s.duration_minutes || 0),
      is_heading: !!s.is_heading
    }))
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  // Task #600: in-context feedback for "Save as Template" — the page-top
  // flash banner sits far above the agenda card, so success/failure was
  // effectively invisible from here.
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateError, setTemplateError] = useState<string | null>(null)
  const [templateSaved, setTemplateSaved] = useState<string | null>(null)
  const [showAddSection, setShowAddSection] = useState(false)
  const [editingDuration, setEditingDuration] = useState<number | null>(null)
  const [durationValue, setDurationValue] = useState('')
  const [showAddHeading, setShowAddHeading] = useState(false)
  const [headingTitle, setHeadingTitle] = useState('')

  const dragItem = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)

  // Task #455: drag-to-reorder attendees. Local state mirrors the server
  // order and re-syncs whenever the prop changes (toggle/reorder reloads).
  const [attendeeList, setAttendeeList] = useState<MeetingAttendee[]>(attendees)
  // Task #516: the 10s meeting heartbeat re-serializes props. Blindly
  // re-syncing local state mid-drag replaces the dragged DOM node (killing
  // the native drag) and a stale in-flight heartbeat can snap the order
  // back right after a drop. Block prop-sync while a drag or reorder PUT
  // is in progress.
  const attendeeSyncBlocked = useRef(false)
  useEffect(() => {
    if (!attendeeSyncBlocked.current) setAttendeeList(attendees)
  }, [attendees])
  const attendeeDragItem = useRef<number | null>(null)
  const attendeeDragOverItem = useRef<number | null>(null)
  // Task #516: drag only initiates from the grip handle (mousedown arms the
  // row's draggable attr) so nested buttons never start or swallow drags.
  const [attendeeDragArmed, setAttendeeDragArmed] = useState<number | null>(null)

  const handleAttendeeDragStart = (index: number, e: React.DragEvent) => {
    // Firefox requires dataTransfer data for a drag to start.
    e.dataTransfer.setData('text/plain', '')
    e.dataTransfer.effectAllowed = 'move'
    attendeeSyncBlocked.current = true
    attendeeDragItem.current = index
  }
  const handleAttendeeDragEnter = (index: number) => { attendeeDragOverItem.current = index }

  const persistAttendeeOrder = (newList: MeetingAttendee[]) => {
    attendeeSyncBlocked.current = true
    setAttendeeList(newList)
    router.put(`/meetings/${meetingId}/attendees/reorder`, {
      attendee_ids: newList.map(a => a.id)
    }, {
      preserveScroll: true,
      onFinish: () => { attendeeSyncBlocked.current = false }
    })
  }

  const handleAttendeeDragEnd = () => {
    const from = attendeeDragItem.current
    const to = attendeeDragOverItem.current
    attendeeDragItem.current = null
    attendeeDragOverItem.current = null
    setAttendeeDragArmed(null)
    if (from === null || to === null || from === to) {
      attendeeSyncBlocked.current = false
      return
    }
    const newList = [...attendeeList]
    const [moved] = newList.splice(from, 1)
    newList.splice(to, 0, moved)
    persistAttendeeOrder(newList)
  }

  // Task #489: keyboard/touch-friendly reorder — up/down buttons persist via
  // the same reorder endpoint as drag.
  const handleAttendeeMove = (index: number, delta: -1 | 1) => {
    const to = index + delta
    if (to < 0 || to >= attendeeList.length) return
    const newList = [...attendeeList]
    ;[newList[index], newList[to]] = [newList[to], newList[index]]
    persistAttendeeOrder(newList)
  }

  const handleDragStart = (index: number) => { dragItem.current = index }
  const handleDragEnter = (index: number) => { dragOverItem.current = index }

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return
    const newItems = [...items]
    const draggedItem = newItems[dragItem.current]
    newItems.splice(dragItem.current, 1)
    newItems.splice(dragOverItem.current, 0, draggedItem)
    dragItem.current = null
    dragOverItem.current = null
    setItems(newItems)
    setHasChanges(true)
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const newItems = [...items]
    ;[newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]]
    setItems(newItems)
    setHasChanges(true)
  }

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return
    const newItems = [...items]
    ;[newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]]
    setItems(newItems)
    setHasChanges(true)
  }

  const handleRemoveSection = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
    setHasChanges(true)
  }

  const handleAddSection = (sectionType: SectionType) => {
    const available = availableSections.find(s => s.section_type === sectionType)
    if (!available) return
    setItems([...items, {
      section_type: sectionType,
      display_name: available.display_name,
      duration_minutes: available.default_duration,
      is_heading: false
    }])
    setShowAddSection(false)
    setHasChanges(true)
  }

  const handleAddHeading = () => {
    const title = headingTitle.trim()
    if (!title) return
    setItems([...items, {
      section_type: 'custom' as SectionType,
      display_name: title,
      title,
      duration_minutes: null,
      is_heading: true
    }])
    setHeadingTitle('')
    setShowAddHeading(false)
    setHasChanges(true)
  }

  const handleDurationSave = (index: number) => {
    const val = parseInt(durationValue, 10)
    if (isNaN(val) || val < 1) return
    const newItems = [...items]
    newItems[index] = { ...newItems[index], duration_minutes: val }
    setItems(newItems)
    setEditingDuration(null)
    setHasChanges(true)
  }

  const handleSaveSections = () => {
    const sectionsData = items.map((item, index) => ({
      section_type: item.section_type,
      duration_minutes: item.is_heading ? null : item.duration_minutes,
      title: item.title || null,
      is_heading: item.is_heading,
      position: index
    }))
    router.post(`/meetings/${meetingId}/update_sections`, { sections: sectionsData })
  }

  const handleSaveAsTemplate = () => {
    const name = templateName.trim()
    if (!name || savingTemplate) return
    setSavingTemplate(true)
    setTemplateError(null)
    router.post(`/meetings/${meetingId}/save_as_template`, {
      template_name: name,
      template_description: templateDescription.trim() || null
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setShowSaveModal(false)
        setTemplateSaved(`Saved as template "${name}". It's now this meeting's template.`)
        setTemplateName('')
        setTemplateDescription('')
      },
      // The modal stays open on failure with the entered name intact so the
      // user can adjust the name and retry.
      onError: (errs) => {
        const raw = (errs as Record<string, string | string[]>).template_name
          ?? (errs as Record<string, string | string[]>).base
        const message = Array.isArray(raw) ? raw[0] : raw
        setTemplateError(message || 'Could not save this agenda as a template. Please try again.')
      },
      onFinish: () => setSavingTemplate(false)
    })
  }

  const openSaveModal = () => {
    setTemplateError(null)
    setTemplateSaved(null)
    setShowSaveModal(true)
  }

  const closeSaveModal = () => {
    if (savingTemplate) return
    setShowSaveModal(false)
    setTemplateError(null)
  }

  const handleStartMeeting = () => {
    router.post(`/meetings/${meetingId}/start`)
  }

  const timedItems = items.filter(i => !i.is_heading)
  const totalDuration = timedItems.reduce((sum, item) => sum + (item.duration_minutes || 0), 0)
  const usedSectionTypes = items.filter(i => !i.is_heading).map(i => i.section_type)
  const currentTemplateName = templates.find(t => t.id === currentTemplateId)?.name
  const startMeetingLabel = meetingDisplayName?.trim()
    ? `Start ${meetingDisplayName.trim()}`
    : currentTemplateName
      ? `Start ${currentTemplateName}`
      : 'Start Meeting'

  return (
    <div className="space-y-6">
      {canStart && (
        <div className="flex justify-center">
          <button
            onClick={canFacilitate && hasChanges ? handleSaveSections : handleStartMeeting}
            disabled={timedItems.length === 0}
            className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors font-heading text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {timedItems.length === 0 ? 'Add sections to start' : hasChanges ? 'Save & Continue' : startMeetingLabel}
          </button>
        </div>
      )}

      {attendees.length > 0 && (
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-text-primary font-heading">Expected Attendees</h3>
              <p className="text-sm text-text-secondary mt-0.5">
                {attendees.filter(a => a.attending).length} of {attendees.length} attending
              </p>
            </div>
          </div>
          <div className="space-y-1">
            {/* allow-unsorted-people: do NOT alphabetize. Attendees keep the
                facilitator's saved drag order (see persistAttendeeOrder). */}
            {attendeeList.map((attendee, index) => {
              const isAttending = attendee.attending
              return (
                <div
                  key={attendee.id}
                  draggable={canFacilitate && attendeeDragArmed === index}
                  onDragStart={e => handleAttendeeDragStart(index, e)}
                  onDragEnter={() => handleAttendeeDragEnter(index)}
                  onDragEnd={handleAttendeeDragEnd}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => e.preventDefault()}
                  data-testid={`attendee-row-${attendee.id}`}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isAttending ? 'hover:bg-[#F5F5F5]/30' : 'bg-stone/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {canFacilitate && (
                      <div
                        className="flex flex-col text-text-muted select-none cursor-grab active:cursor-grabbing"
                        onMouseDown={() => setAttendeeDragArmed(index)}
                        onMouseUp={() => setAttendeeDragArmed(null)}
                        data-testid={`attendee-drag-handle-${attendee.id}`}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M7 2a2 2 0 10.001 4.001A2 2 0 007 2zm0 6a2 2 0 10.001 4.001A2 2 0 007 8zm0 6a2 2 0 10.001 4.001A2 2 0 007 14zm6-8a2 2 0 10-.001-4.001A2 2 0 0013 6zm0 2a2 2 0 10.001 4.001A2 2 0 0013 8zm0 6a2 2 0 10.001 4.001A2 2 0 0013 14z" />
                        </svg>
                      </div>
                    )}
                    <div className={isAttending ? '' : 'grayscale'}>
                      <UserAvatar user={attendee.user} size="md" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isAttending ? 'text-text-primary' : 'text-text-secondary line-through'}`}>
                        {attendee.user.full_name}
                      </p>
                      <p className="text-sm text-text-muted">
                        {attendee.user.role}{attendee.user.is_executive_leadership ? ' · EL' : ''}
                      </p>
                    </div>
                  </div>
                  {canFacilitate && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleAttendeeMove(index, -1)}
                          disabled={index === 0}
                          className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
                          title="Move up"
                          aria-label={`Move ${attendee.user.full_name} up`}
                          data-testid={`attendee-move-up-${attendee.id}`}
                        >↑</button>
                        <button
                          onClick={() => handleAttendeeMove(index, 1)}
                          disabled={index === attendeeList.length - 1}
                          className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
                          title="Move down"
                          aria-label={`Move ${attendee.user.full_name} down`}
                          data-testid={`attendee-move-down-${attendee.id}`}
                        >↓</button>
                      </div>
                    <button
                      onClick={() => {
                        router.put(`/meetings/${meetingId}/attendees/${attendee.id}`, {
                          attending: !isAttending
                        }, { preserveScroll: true })
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isAttending
                          ? 'text-status-complete bg-status-complete/10 hover:bg-status-complete/20'
                          : 'text-text-muted bg-charcoal/5 hover:bg-[#351E1D]/10'
                      }`}
                    >
                      {isAttending ? 'Attending' : 'Absent'}
                    </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-text-primary font-heading">Meeting Agenda</h2>
            <p className="text-sm text-text-secondary mt-1">
              {timedItems.length} sections · {totalDuration} minutes total
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <button
                onClick={handleSaveSections}
                className="px-4 py-2 bg-charcoal text-white text-sm font-medium rounded-lg hover:bg-[#351E1D]/90 transition-colors"
              >
                Save Changes
              </button>
            )}
            {canSaveAsTemplate && (
              <button
                onClick={openSaveModal}
                className="px-4 py-2 border border-charcoal/20 text-text-primary text-sm font-medium rounded-lg hover:bg-[#F5F5F5]/30 transition-colors"
              >
                Save as Template
              </button>
            )}
          </div>
        </div>

        {templateSaved && (
          <div
            className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-status-complete/30 bg-status-complete/10 px-4 py-3"
            data-testid="template-save-success"
          >
            <p className="text-sm text-text-primary">{templateSaved}</p>
            <button
              onClick={() => setTemplateSaved(null)}
              className="text-sm text-text-secondary hover:text-text-primary"
              aria-label="Dismiss confirmation"
            >✕</button>
          </div>
        )}

        <div className="space-y-2">
          {items.map((item, index) => {
            if (item.is_heading) {
              // Task #261: non-interactive label band — no drag, no duration,
              // no move/remove unless the facilitator explicitly chooses
              // remove. Excluded from timed-section count and total time.
              return (
                <div
                  key={`hdr-${item.id ?? index}`}
                  className="flex items-center gap-3 px-4 py-2 mt-2 rounded-md bg-charcoal/5 border-l-4 border-charcoal/40 group"
                  data-testid="agenda-heading-row"
                >
                  <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                      {item.title || item.display_name}
                    </p>
                  </div>
                  {canFacilitate && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
                        title="Move up"
                      >↑</button>
                      <button
                        onClick={() => handleMoveDown(index)}
                        disabled={index === items.length - 1}
                        className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
                        title="Move down"
                      >↓</button>
                      <button
                        onClick={() => handleRemoveSection(index)}
                        className="p-1 text-text-muted hover:text-pinnacle-red transition-colors"
                        title="Remove heading"
                      >✕</button>
                    </div>
                  )}
                </div>
              )
            }
            return (
            <div
              key={`${item.section_type}-${item.id ?? index}`}
              draggable={canFacilitate}
              onDragStart={() => handleDragStart(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
              className="flex items-center gap-3 bg-[#F5F5F5]/20 border border-stone/20 rounded-lg px-4 py-3 group hover:border-charcoal/30 transition-colors cursor-grab active:cursor-grabbing"
            >
              <div className="flex flex-col text-text-muted select-none">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 2a2 2 0 10.001 4.001A2 2 0 007 2zm0 6a2 2 0 10.001 4.001A2 2 0 007 8zm0 6a2 2 0 10.001 4.001A2 2 0 007 14zm6-8a2 2 0 10-.001-4.001A2 2 0 0013 6zm0 2a2 2 0 10.001 4.001A2 2 0 0013 8zm0 6a2 2 0 10.001 4.001A2 2 0 0013 14z" />
                </svg>
              </div>

              <div className="w-7 h-7 bg-charcoal/10 rounded-full flex items-center justify-center text-sm font-semibold text-text-secondary flex-shrink-0">
                {timedItems.indexOf(item) + 1}
              </div>

              <div className="flex-1">
                <p className="text-sm font-medium text-text-primary">{item.title || item.display_name}</p>
              </div>

              <div className="flex items-center gap-2">
                {!canFacilitate ? (
                  <span className="text-sm text-text-secondary">{item.duration_minutes ?? 0}m</span>
                ) : editingDuration === index ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="1"
                      value={durationValue}
                      onChange={e => setDurationValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleDurationSave(index)
                        if (e.key === 'Escape') setEditingDuration(null)
                      }}
                      className="w-16 px-2 py-1 text-sm border border-charcoal/20 rounded text-center"
                      autoFocus
                    />
                    <span className="text-sm text-text-muted">min</span>
                    <button onClick={() => handleDurationSave(index)} className="text-status-on-track text-sm">✓</button>
                    <button onClick={() => setEditingDuration(null)} className="text-text-muted text-sm">✕</button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingDuration(index)
                      setDurationValue(String(item.duration_minutes ?? 5))
                    }}
                    className="px-2 py-1 text-sm text-text-secondary bg-charcoal/5 rounded hover:bg-[#351E1D]/10 transition-colors"
                    title="Click to edit duration"
                  >
                    {item.duration_minutes ?? 0}m
                  </button>
                )}
              </div>

              {canFacilitate && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleMoveUp(index)} disabled={index === 0} className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30" title="Move up">↑</button>
                  <button onClick={() => handleMoveDown(index)} disabled={index === items.length - 1} className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30" title="Move down">↓</button>
                  <button onClick={() => handleRemoveSection(index)} className="p-1 text-text-muted hover:text-pinnacle-red transition-colors" title="Remove section">✕</button>
                </div>
              )}
            </div>
            )
          })}
        </div>

        {canFacilitate && (
          <div className="mt-4 space-y-2">
            {showAddSection ? (
              <div className="border border-dashed border-charcoal/20 rounded-lg p-4">
                <p className="text-sm font-medium text-text-primary mb-3">Add a section:</p>
                <div className="grid grid-cols-2 gap-2">
                  {availableSections.map(section => {
                    const alreadyUsed = usedSectionTypes.includes(section.section_type)
                    return (
                      <button
                        key={section.section_type}
                        onClick={() => handleAddSection(section.section_type)}
                        className={`flex items-center justify-between px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                          alreadyUsed
                            ? 'border-stone/10 bg-[#F5F5F5]/20 text-text-muted'
                            : 'border-charcoal/20 hover:bg-[#F5F5F5]/30 text-text-primary hover:border-charcoal/40'
                        }`}
                      >
                        <span>{section.display_name}</span>
                        <span className="text-sm text-text-muted">{section.default_duration}m</span>
                      </button>
                    )
                  })}
                </div>
                <button onClick={() => setShowAddSection(false)} className="mt-3 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
              </div>
            ) : showAddHeading ? (
              <div className="border border-dashed border-charcoal/20 rounded-lg p-4">
                <p className="text-sm font-medium text-text-primary mb-3">Add a heading (non-timed label):</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={headingTitle}
                    onChange={e => setHeadingTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleAddHeading()
                      if (e.key === 'Escape') { setShowAddHeading(false); setHeadingTitle('') }
                    }}
                    placeholder="e.g., Previous Month Review"
                    className="flex-1 px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary"
                    autoFocus
                  />
                  <button
                    onClick={handleAddHeading}
                    disabled={!headingTitle.trim()}
                    className="px-3 py-2 bg-charcoal text-white text-sm rounded-lg disabled:opacity-50"
                  >Add</button>
                  <button onClick={() => { setShowAddHeading(false); setHeadingTitle('') }} className="text-sm text-text-secondary">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddSection(true)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 border border-dashed border-charcoal/20 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:border-charcoal/40 transition-colors"
                >+ Add Section</button>
                <button
                  onClick={() => setShowAddHeading(true)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 border border-dashed border-charcoal/20 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:border-charcoal/40 transition-colors"
                >+ Add Heading</button>
              </div>
            )}
          </div>
        )}
      </div>

      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeSaveModal}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()} data-testid="save-template-modal">
            <h3 className="text-lg font-semibold text-text-primary font-heading mb-4">Save as Template</h3>
            {templateError && (
              <div
                className="mb-4 rounded-lg border border-pinnacle-red/30 bg-pinnacle-red/10 px-4 py-3"
                data-testid="template-save-error"
              >
                <p className="text-sm text-text-primary">{templateError}</p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="save-template-name" className="block text-sm font-medium text-text-primary mb-1">Template Name</label>
                <input
                  id="save-template-name"
                  type="text"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  disabled={savingTemplate}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red disabled:opacity-60"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="save-template-description" className="block text-sm font-medium text-text-primary mb-1">Description (optional)</label>
                <textarea
                  id="save-template-description"
                  value={templateDescription}
                  onChange={e => setTemplateDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeSaveModal}
                disabled={savingTemplate}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={handleSaveAsTemplate}
                disabled={!templateName.trim() || savingTemplate}
                className="px-4 py-2 bg-pinnacle-red text-white text-sm font-medium rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
              >{savingTemplate ? 'Saving…' : 'Save Template'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
